/**
 * Realtime channel helpers for messaging.
 *
 * Channel conventions (mirrors BACKEND_REQUIREMENTS_MESSAGING.md §5.1):
 *   - `thread:{threadId}`   — INSERTs on `public.messages` for a single thread.
 *   - `inbox:{designerId}`  — INSERT/UPDATE/DELETE on `public.message_threads`
 *                             for a single designer.
 *
 * The `supabase_realtime` publication already includes both tables per
 * backend-messaging-R1.md, so postgres_changes streams out of the box.
 */
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import type { DbMessage, DbThread } from '../api/messagingApi';

export type ThreadEventType = 'INSERT' | 'UPDATE' | 'DELETE';

export interface ThreadChannelHandlers {
  onMessageInsert: (row: DbMessage) => void;
}

export interface InboxChannelHandlers {
  onThreadChange: (row: DbThread, eventType: ThreadEventType) => void;
}

/**
 * A subscribed Realtime channel paired with a `ready` promise that resolves
 * once the websocket has ACK'd the subscription (`status === 'SUBSCRIBED'`).
 *
 * Callers should `await ready` before issuing the initial fetch so any events
 * fired during the connect window aren't dropped on the floor. The promise is
 * intentionally resolved (not rejected) on error/timeout/close so callers can
 * still fall through to a best-effort fetch instead of hanging forever.
 */
export interface SubscribedChannel {
  channel: RealtimeChannel;
  ready: Promise<void>;
}

/** Hard cap so a flaky connection never blocks the initial fetch. */
const READY_TIMEOUT_MS = 5000;

const wireReady = (channel: RealtimeChannel): Promise<void> =>
  new Promise<void>((resolve) => {
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    const timer = window.setTimeout(settle, READY_TIMEOUT_MS);
    channel.subscribe((status) => {
      // Resolve on first terminal-ish status. SUBSCRIBED = success;
      // CHANNEL_ERROR / TIMED_OUT / CLOSED = give up so the caller can proceed.
      if (
        status === 'SUBSCRIBED' ||
        status === 'CHANNEL_ERROR' ||
        status === 'TIMED_OUT' ||
        status === 'CLOSED'
      ) {
        window.clearTimeout(timer);
        settle();
      }
    });
  });

export function subscribeThreadChannel(
  threadId: string,
  handlers: ThreadChannelHandlers,
): SubscribedChannel {
  const channel = supabase.channel(`thread:${threadId}`).on(
    'postgres_changes',
    {
      event: 'INSERT',
      schema: 'public',
      table: 'messages',
      filter: `thread_id=eq.${threadId}`,
    },
    (payload) => handlers.onMessageInsert(payload.new as DbMessage),
  );
  return { channel, ready: wireReady(channel) };
}

export function subscribeInboxChannel(
  designerId: string,
  handlers: InboxChannelHandlers,
): SubscribedChannel {
  const channel = supabase.channel(`inbox:${designerId}`).on(
    'postgres_changes',
    {
      event: '*',
      schema: 'public',
      table: 'message_threads',
      filter: `designer_id=eq.${designerId}`,
    },
    (payload) => {
      const row = ((payload.new ?? payload.old) as DbThread | null) ?? null;
      if (!row) return;
      handlers.onThreadChange(row, payload.eventType as ThreadEventType);
    },
  );
  return { channel, ready: wireReady(channel) };
}

/** Buyer/client inbox: watches `message_threads` rows where `client_id` matches. */
export function subscribeClientInboxChannel(
  clientId: string,
  handlers: InboxChannelHandlers,
): SubscribedChannel {
  const channel = supabase.channel(`inbox:client:${clientId}`).on(
    'postgres_changes',
    {
      event: '*',
      schema: 'public',
      table: 'message_threads',
      filter: `client_id=eq.${clientId}`,
    },
    (payload) => {
      const row = ((payload.new ?? payload.old) as DbThread | null) ?? null;
      if (!row) return;
      handlers.onThreadChange(row, payload.eventType as ThreadEventType);
    },
  );
  return { channel, ready: wireReady(channel) };
}

export function unsubscribeChannel(channel: RealtimeChannel | null): void {
  if (channel) void supabase.removeChannel(channel);
}
